import { gql } from '@apollo/client';

import {
  KOMMO_LEAD_CARD_FIELDS,
  KOMMO_LEAD_MOVED_FIELDS,
  KOMMO_USER_FIELDS,
  LEAD_LADDER_RUN_FIELDS,
  LEAD_SMS_STATE_FIELDS,
} from '../fragments/crm';
import { SMS_MESSAGE_FIELDS } from '../fragments/sms';

/**
 * ТЗ 0084 — лиды Kommo в ERP. Имена операций отличаются от полей бэкенда
 * (`sendLeadSms` → `WriteLeadSms` и т. д.), иначе codegen склеил бы тип
 * результата с типом поля.
 */

/**
 * SMS лиду. Ответ несёт состояние карточки целиком — оно пишется в запрос
 * `LeadSmsState` руками (`useLeadSms`). `requestKey` гасит двойной клик и
 * повтор после обрыва сети.
 */
export const WriteLeadSmsMutation = gql`
  ${SMS_MESSAGE_FIELDS}
  ${LEAD_SMS_STATE_FIELDS}
  mutation WriteLeadSms(
    $leadId: ID!
    $text: String!
    $link: LeadSmsLinkInput
    $templateId: ID
    $requestKey: String
  ) {
    sendLeadSms(
      leadId: $leadId
      text: $text
      link: $link
      templateId: $templateId
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      message {
        ...SmsMessageFields
      }
      state {
        ...LeadSmsStateFields
      }
    }
  }
`;

/** Перечитать лида из Kommo сейчас (сервер — не чаще раза в минуту). */
export const RefreshLeadMutation = gql`
  ${KOMMO_LEAD_CARD_FIELDS}
  mutation RefreshLead($id: ID!) {
    refreshKommoLead(id: $id) {
      success
      customErrors {
        field
        code
        message
      }
      refreshed
      lead {
        ...KommoLeadCardFields
      }
    }
  }
`;

/** Admin: синхронизация сейчас — в фоне, ответ «поставлено в очередь». */
export const SyncLeadsNowMutation = gql`
  mutation SyncLeadsNow($full: Boolean) {
    syncKommoLeads(full: $full) {
      success
      customErrors {
        field
        code
        message
      }
      queued
    }
  }
`;

/**
 * Admin: сотрудник ERP за пользователем Kommo. `locked=false` — вернуть
 * автоматике. Ответ — та же запись `KommoUserType:<id>`.
 */
export const SaveKommoUserMappingMutation = gql`
  ${KOMMO_USER_FIELDS}
  mutation SaveKommoUserMapping(
    $kommoUserId: ID!
    $userId: ID
    $locked: Boolean
  ) {
    setKommoUserMapping(
      kommoUserId: $kommoUserId
      userId: $userId
      locked: $locked
    ) {
      success
      customErrors {
        field
        code
        message
      }
      user {
        ...KommoUserFields
      }
    }
  }
`;

/** Admin: повторить примечания, которые Kommo не принял. */
export const RetryLeadNotesMutation = gql`
  mutation RetryLeadNotes {
    retryKommoNotes {
      success
      customErrors {
        field
        code
        message
      }
      count
    }
  }
`;

/** SMS кандидату дофинансирования — зеркало `WriteLeadSms`. */
export const WriteCandidateSmsMutation = gql`
  ${SMS_MESSAGE_FIELDS}
  ${LEAD_SMS_STATE_FIELDS}
  mutation WriteCandidateSms(
    $candidateId: ID!
    $text: String!
    $link: LeadSmsLinkInput
    $templateId: ID
    $requestKey: String
  ) {
    sendFundingCandidateSms(
      candidateId: $candidateId
      text: $text
      link: $link
      templateId: $templateId
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      message {
        ...SmsMessageFields
      }
      state {
        ...LeadSmsStateFields
      }
    }
  }
`;

/**
 * Этап 2: поставить лида на SMS-лесенку. Активный запуск того же вида
 * заменяется; `warning` — почему сейчас не шлёт (запуск создан всё равно).
 */
export const StartLadderForLeadMutation = gql`
  ${LEAD_LADDER_RUN_FIELDS}
  mutation StartLadderForLead(
    $leadId: ID!
    $kind: String!
    $days: Int
    $requestKey: String
  ) {
    startLeadLadder(
      leadId: $leadId
      kind: $kind
      days: $days
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      warning
      ladder {
        ...LeadLadderRunFields
      }
    }
  }
`;

/** Этап 2: остановить запуск лесенки (возобновить можно). */
export const StopLeadLadderRunMutation = gql`
  ${LEAD_LADDER_RUN_FIELDS}
  mutation StopLeadLadderRun($runId: ID!) {
    stopLeadLadder(runId: $runId) {
      success
      customErrors {
        field
        code
        message
      }
      ladder {
        ...LeadLadderRunFields
      }
    }
  }
`;

/** Этап 2: возобновить остановленный запуск. */
export const ResumeLeadLadderRunMutation = gql`
  ${LEAD_LADDER_RUN_FIELDS}
  mutation ResumeLeadLadderRun($runId: ID!) {
    resumeLeadLadder(runId: $runId) {
      success
      customErrors {
        field
        code
        message
      }
      ladder {
        ...LeadLadderRunFields
      }
    }
  }
`;

/**
 * Этап 3: письмо лиду со своей рабочей почты. `{link}` в тексте — ссылка на
 * анкету дофинансирования. Повтор `requestKey` — то же письмо.
 */
export const WriteLeadEmailMutation = gql`
  mutation WriteLeadEmail(
    $leadId: ID!
    $mailboxEmail: String!
    $subject: String!
    $text: String
    $html: String
    $attachmentIds: [ID]
    $templateId: ID
    $requestKey: String
  ) {
    sendLeadEmail(
      leadId: $leadId
      mailboxEmail: $mailboxEmail
      subject: $subject
      text: $text
      html: $html
      attachmentIds: $attachmentIds
      templateId: $templateId
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      conversationId
    }
  }
`;

/**
 * Этап 3: «Пригласить к анкете» — письмо со ссылкой на анкету и (по
 * галочке) лесенка «Анкета». Письмо не ушло — лесенки нет; SMS сейчас не
 * могут — письмо ушло, причина в `ladderWarning`.
 */
export const InviteLeadToFormMutation = gql`
  ${LEAD_LADDER_RUN_FIELDS}
  mutation InviteLeadToForm(
    $leadId: ID!
    $mailboxEmail: String!
    $subject: String!
    $text: String
    $html: String
    $attachmentIds: [ID]
    $templateId: ID
    $startLadder: Boolean
    $days: Int
    $requestKey: String
  ) {
    inviteLeadToFundingForm(
      leadId: $leadId
      mailboxEmail: $mailboxEmail
      subject: $subject
      text: $text
      html: $html
      attachmentIds: $attachmentIds
      templateId: $templateId
      startLadder: $startLadder
      days: $days
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      conversationId
      ladderWarning
      ladder {
        ...LeadLadderRunFields
      }
    }
  }
`;

/**
 * ТЗ 0086 — связать лида вручную с заявкой дофинансирования (`what:
 * candidate`) или студентом (`what: student`); `targetId: null` — отвязать.
 * Ручная связь запирается: робот её больше не трогает. Ответ — лид целиком,
 * он же запись кэша карточки и строки списка.
 */
export const LinkKommoLeadMutation = gql`
  ${KOMMO_LEAD_CARD_FIELDS}
  mutation ErpLinkKommoLead($leadId: ID!, $what: String!, $targetId: ID) {
    linkKommoLead(leadId: $leadId, what: $what, targetId: $targetId) {
      success
      customErrors {
        field
        code
        message
      }
      lead {
        ...KommoLeadCardFields
      }
    }
  }
`;

/** ТЗ 0086 — вернуть связь роботу: снять замок и пересверить. */
export const ResetKommoLeadLinkMutation = gql`
  ${KOMMO_LEAD_CARD_FIELDS}
  mutation ErpResetKommoLeadLink($leadId: ID!, $what: String!) {
    resetKommoLeadLink(leadId: $leadId, what: $what) {
      success
      customErrors {
        field
        code
        message
      }
      lead {
        ...KommoLeadCardFields
      }
    }
  }
`;

/**
 * ТЗ 0106 — перенести лида на этап (запись в Kommo, потом зеркало). В 143
 * Kommo требует причину отказа — `lossReasonId`. Ответ — та же запись кэша
 * `KommoLeadType:<id>`: строка таблицы, карточка доски и окно лида
 * обновляются сами.
 */
export const MoveKommoLeadStageMutation = gql`
  ${KOMMO_LEAD_MOVED_FIELDS}
  mutation MoveKommoLeadStage($id: ID!, $statusId: ID!, $lossReasonId: ID) {
    moveKommoLead(id: $id, statusId: $statusId, lossReasonId: $lossReasonId) {
      success
      changed
      customErrors {
        field
        message
        code
      }
      lead {
        ...KommoLeadMovedFields
      }
    }
  }
`;

/** ТЗ 0106 — сменить ответственного лида в Kommo (`userId` — строка Kommo). */
export const AssignKommoLeadResponsibleMutation = gql`
  ${KOMMO_LEAD_MOVED_FIELDS}
  mutation AssignKommoLeadResponsible($id: ID!, $userId: ID!) {
    setKommoLeadResponsible(id: $id, userId: $userId) {
      success
      changed
      customErrors {
        field
        message
        code
      }
      lead {
        ...KommoLeadMovedFields
      }
    }
  }
`;

/**
 * ТЗ 0117 — AI-черновик письма лиду по разобранному звонку Aplora. Тот же
 * `requestKey` на повторный клик — тот же черновик; «Сгенерировать заново» —
 * `regenerate: true` и новый ключ.
 */
export const DraftLeadAploraFollowupMutation = gql`
  mutation DraftLeadAploraFollowup(
    $leadId: ID!
    $callId: ID!
    $regenerate: Boolean
    $requestKey: String
  ) {
    aploraFollowupDraft(
      leadId: $leadId
      callId: $callId
      regenerate: $regenerate
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        message
        code
      }
      draft {
        emailSubject
        emailBody
        messengerText
        cached
      }
    }
  }
`;
